import React, { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { Upload, Image as ImageIcon, Video, Sliders, RefreshCw, Download, MonitorPlay } from 'lucide-react';

export const PayloadMatrix: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'video' | null>(null);
  
  // Basic edit states
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      const url = URL.createObjectURL(selected);
      setPreviewUrl(url);
      setFileType(selected.type.startsWith('video/') ? 'video' : 'image');
      
      // Reset filters
      setBrightness(100);
      setContrast(100);
      setSaturation(100);
    }
  };

  const handleReset = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
  };

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  const filterStyle = {
    filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`
  };

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-cyan-500/20 pb-6 mb-2 gap-4 shrink-0">
        <div>
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest block mb-2">INTEGRATION NODE 9: ACTIVE</span>
          <h2 className="text-2xl font-bold text-white uppercase tracking-widest font-sans drop-shadow-md flex items-center gap-3">
            <MonitorPlay className="w-6 h-6 text-cyan-400 shrink-0" />
            DYNAMIC PAYLOAD MATRIX
          </h2>
        </div>
        
        <div className="flex items-center gap-3 shrink-0">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept="image/*,video/*" 
            className="hidden" 
          />
          <button 
            onClick={triggerUpload}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600/80 to-blue-800/80 hover:from-cyan-500 hover:to-blue-700 border border-cyan-400/50 transition-all px-6 py-2.5 rounded shadow-[0_0_15px_rgba(34,211,238,0.3)] text-white group"
          >
            <Upload className="w-4 h-4 shadow-[0_0_10px_rgba(255,255,255,0.5)] bg-white/10 rounded" />
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold">LOAD ASSET</span>
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0">
        {/* Preview Area */}
        <div className="flex-1 bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl flex flex-col overflow-hidden relative shadow-lg group">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/10 via-transparent to-transparent pointer-events-none"></div>
          
          <div className="h-12 border-b border-white/5 bg-black/40 flex items-center px-4 shrink-0 justify-between relative z-10">
            <div className="flex items-center gap-2 font-mono text-[10px] text-slate-400 uppercase tracking-widest">
              {fileType === 'video' ? <Video className="w-4 h-4 text-cyan-400" /> : <ImageIcon className="w-4 h-4 text-cyan-400" />}
              <span>{file ? file.name : 'NO PAYLOAD LOADED'}</span>
            </div>
            {file && (
              <span className="font-mono text-[9px] text-cyan-500 bg-cyan-900/30 px-2 py-0.5 rounded border border-cyan-500/30">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </span>
            )}
          </div>
          
          <div className="flex-1 flex justify-center items-center p-6 bg-black relative z-10 overflow-hidden">
            {!previewUrl ? (
              <div className="flex flex-col items-center justify-center text-slate-500 gap-4 opacity-50">
                <MonitorPlay className="w-16 h-16" />
                <span className="font-mono text-xs uppercase tracking-widest">AWAITING MATRIX UPLINK</span>
              </div>
            ) : fileType === 'video' ? (
              <video 
                src={previewUrl} 
                controls 
                style={filterStyle}
                className="max-w-full max-h-full object-contain rounded drop-shadow-[0_0_20px_rgba(34,211,238,0.15)]"
              />
            ) : (
              <img 
                src={previewUrl} 
                alt="Payload Preview" 
                style={filterStyle}
                className="max-w-full max-h-full object-contain rounded drop-shadow-[0_0_20px_rgba(34,211,238,0.15)]" 
              />
            )}
          </div>
        </div>

        {/* Controls Area */}
        <div className="w-full lg:w-80 flex flex-col gap-4 shrink-0">
          <div className="bg-[#0b0f19] border border-cyan-500/20 rounded-2xl p-6 shadow-xl flex flex-col">
            <div className="flex items-center gap-2 mb-6 border-b border-white/5 pb-4">
              <Sliders className="w-4 h-4 text-cyan-400" />
              <h3 className="font-mono font-bold text-[11px] text-white uppercase tracking-widest">VISUAL METRICS</h3>
            </div>
            
            <div className="space-y-6">
              {/* Brightness */}
              <div className="space-y-3">
                <div className="flex justify-between font-mono text-[10px] uppercase tracking-wider">
                  <span className="text-slate-400">BRIGHTNESS</span>
                  <span className="text-cyan-400">{brightness}%</span>
                </div>
                <input 
                  type="range" 
                  min="0" max="200" 
                  value={brightness}
                  onChange={(e) => setBrightness(Number(e.target.value))}
                  disabled={!file}
                  className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                />
              </div>

              {/* Contrast */}
              <div className="space-y-3">
                <div className="flex justify-between font-mono text-[10px] uppercase tracking-wider">
                  <span className="text-slate-400">CONTRAST</span>
                  <span className="text-cyan-400">{contrast}%</span>
                </div>
                <input 
                  type="range" 
                  min="0" max="200" 
                  value={contrast}
                  onChange={(e) => setContrast(Number(e.target.value))}
                  disabled={!file}
                  className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                />
              </div>

              {/* Saturation */}
              <div className="space-y-3">
                <div className="flex justify-between font-mono text-[10px] uppercase tracking-wider">
                  <span className="text-slate-400">SATURATION</span>
                  <span className="text-cyan-400">{saturation}%</span>
                </div>
                <input 
                  type="range" 
                  min="0" max="200" 
                  value={saturation}
                  onChange={(e) => setSaturation(Number(e.target.value))}
                  disabled={!file}
                  className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                />
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button 
                onClick={handleReset}
                disabled={!file}
                className="flex-1 flex items-center justify-center gap-2 bg-black border border-white/10 hover:border-white/30 text-slate-300 hover:text-white transition-all py-2 rounded font-mono text-[10px] uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-3 h-3" />
                <span>RESTORE</span>
              </button>
              
              <button
                disabled={!file}
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600/80 to-teal-800/80 hover:from-emerald-500 hover:to-teal-700 border border-emerald-400/50 text-white transition-all py-2 rounded font-mono text-[10px] uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_10px_rgba(16,185,129,0.2)]"
              >
                <Download className="w-3 h-3" />
                <span>EXPORT</span>
              </button>
            </div>
            
          </div>
          
          {/* Output Config */}
          <div className="bg-[#0b0f19] border border-indigo-500/20 rounded-2xl p-5 shadow-xl">
             <div className="flex items-center justify-between mb-2">
                <span className="font-mono font-bold text-[10px] text-indigo-400 uppercase tracking-widest">RENDER ENGINE</span>
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
             </div>
             <p className="font-mono text-[9px] text-slate-400 uppercase tracking-wider leading-relaxed">
               The Dynamic Payload Matrix is synchronized with the NexoraX NexoraX Core. Exporting will apply the visual metrics and encode the payload in 256-bit secure format for extraction via the primary pipeline.
             </p>
          </div>
        </div>
      </div>
    </div>
  );
};
